import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../redux/helpers/axios";

const STATUS_STYLES = {
  PENDING: "bg-indigo-100 text-indigo-700",
  APPROVED: "bg-green-100 text-green-700",
  PROCESSING: "bg-teal-100 text-teal-700",
  PURCHASED: "bg-indigo-700 text-indigo-100",
  SHIPPED_BY_SELLER: "bg-violet-100 text-violet-700",
  AT_CHINA_WAREHOUSE: "bg-purple-100 text-purple-700",
  IN_TRANSIT: "bg-sky-100 text-sky-700",
  AT_DESTINATION_WAREHOUSE: "bg-green-100 text-green-700",
  DELIVERY_REQUESTED: "bg-teal-100 text-teal-700",
  DELIVERY_PROCESSING: "bg-teal-100 text-teal-700",
  READY_FOR_DELIVERY: "bg-lime-100 text-lime-700",
  HANDOVER_TO_COURIER: "bg-yellow-100 text-yellow-700",
  DELIVERED: "bg-emerald-100 text-emerald-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const STATUS_UPDATE_MSG = {
  PROCESSING: "Your order item is being processed.",
  SHIPPED_BY_SELLER: "Your order item has been shipped by the seller.",
  AT_CHINA_WAREHOUSE:
    "Good News! Your order item has arrived at our China warehouse.",
  IN_TRANSIT: "Your order item is in transit.",
  AT_DESTINATION_WAREHOUSE:
    "Your order item has been received at the destination warehouse.",
  DELIVERY_REQUESTED: "Delivery has been requested for the order item.",
  DELIVERY_PROCESSING: "Delivery is being processed for the order item.",
  READY_FOR_DELIVERY: "Your order item is ready for delivery.",
  HANDOVER_TO_COURIER: "Your order item has been handed over to the courier.",
  DELIVERED: "Your order item has been delivered.",
};

const INVOICE_STATUS_STYLES = {
  PAID: "bg-emerald-100 text-emerald-700",
  UNPAID: "bg-red-100 text-red-700",
  PARTIALLY_PAID: "bg-amber-100 text-amber-700",
  REFUNDED: "bg-blue-100 text-blue-700",
  PARTIALLY_REFUNDED: "bg-sky-100 text-sky-700",
  CANCELLED: "bg-gray-100 text-gray-700",
};

const StatusBadge = ({ status, styles }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
      styles[status] || "bg-gray-50 text-gray-700 ring-gray-600/20"
    }`}
  >
    {status?.replace(/_/g, " ")}
  </span>
);

const ConfirmModal = ({
  open,
  title,
  message,
  confirmLabel,
  confirmColor,
  loading,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="mt-1.5 text-sm text-gray-500">{message}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Close
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-3 py-1.5 text-xs font-medium rounded-md text-white disabled:opacity-40 ${confirmColor}`}
          >
            {loading ? "Please wait..." : "Yes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderUpdateModal = ({ open, item, onClose, onUpdated }) => {
  const [selectedStatus, setSelectedStatus] = useState(
    item?.status || "PROCESSING",
  );
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(null);

  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);

  const [sourceOrderId, setSourceOrderId] = useState(
    item?.sourceOrderId || null,
  );
  const [sourceOrderIdInput, setSourceOrderIdInput] = useState("");
  const [savingSourceOrderId, setSavingSourceOrderId] = useState(false);
  const [sourceOrderIdError, setSourceOrderIdError] = useState(null);

  const [trackingInput, setTrackingInput] = useState("");
  const [savingTracking, setSavingTracking] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [trackingSuccess, setTrackingSuccess] = useState(null);
  const [addedTrackings, setAddedTrackings] = useState([]);
  const [weight, setWeight] = useState(item?.shipment?.totalWeight || "0");

  // reset local state whenever the modal is (re)opened for an item
  useEffect(() => {
    if (open) {
      setSelectedStatus(item?.status || "PROCESSING");
      setUpdateError(null);
      setUpdateSuccess(null);
      setEmailError(null);
      setEmailSuccess(null);
      setSourceOrderId(item?.sourceOrderId || null);
      setSourceOrderIdInput("");
      setSourceOrderIdError(null);
      setTrackingInput("");
      setTrackingError(null);
      setTrackingSuccess(null);
      setAddedTrackings([]);
    }
  }, [open, item]);

  if (!open) return null;

  const handleUpdateStatus = async () => {
    setUpdating(true);
    setUpdateError(null);
    setUpdateSuccess(null);
    try {
      await axiosInstance.post(
        `/admin/services/order/update-status/${item.itemId}`,
        {
          status: selectedStatus,
          msg: STATUS_UPDATE_MSG[selectedStatus],
        },
      );
      setUpdateSuccess("Status updated successfully.");
      onUpdated?.();
    } catch (err) {
      setUpdateError(err?.response?.data?.msg || "Failed to update status.");
    } finally {
      setUpdating(false);
    }
  };

  const handleSendPurchaseEmail = async () => {
    setSendingEmail(true);
    setEmailError(null);
    setEmailSuccess(null);
    try {
      await axiosInstance.get(`/admin/services/order/purchase/${item.itemId}`);
      setEmailSuccess("Purchase email sent.");
    } catch (err) {
      setEmailError(
        err?.response?.data?.msg || "Failed to send purchase email.",
      );
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAddSourceOrderId = async () => {
    const value = sourceOrderIdInput.trim();
    if (!value) return;
    setSavingSourceOrderId(true);
    setSourceOrderIdError(null);
    try {
      const res = await axiosInstance.post(
        `/admin/services/order/add-source-order-id/${item.itemId}`,
        { sourceOrderId: value },
      );
      setSourceOrderId(res.data?.orderItem?.sourceOrderId || value);
      onUpdated?.();
    } catch (err) {
      setSourceOrderIdError(
        err?.response?.data?.msg || "Failed to save source order ID.",
      );
    } finally {
      setSavingSourceOrderId(false);
    }
  };

  const handleAddTracking = async () => {
    const value = trackingInput.trim();
    if (!value || !item?.shipment?.id) return;
    setSavingTracking(true);
    setTrackingError(null);
    setTrackingSuccess(null);
    try {
      const res = await axiosInstance.post(
        `/admin/services/order/add-tracking/${item.shipment.id}`,
        { internalTrackingNumber: value },
      );
      if (res.data?.tracking) {
        setAddedTrackings((prev) => [...prev, res.data.tracking]);
      }
      setTrackingSuccess("Tracking number added.");
      setTrackingInput("");
      onUpdated?.();
    } catch (err) {
      setTrackingError(
        err?.response?.data?.msg || "Failed to add tracking number.",
      );
    } finally {
      setSavingTracking(false);
    }
  };

  const handleUpdateWeight = async () => {
    if (!item?.shipment?.id) return;

    try {
      const res = await axiosInstance.post(
        `/admin/services/order/update-weight/${item.shipment.id}`,
        { weight: Number(weight) },
      );
      if (res.status === 200) {
        onUpdated?.();
      }
    } catch (err) {
      alert(err?.response?.data?.msg || "Failed to update weight.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Manage Order Item #{item.itemId}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Order #{item.order?.orderId} | {item.order?.user?.email || "N/A"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <div className="flex flex-1 overflow-y-auto">
          {/* Left panel */}
          <div className="w-full sm:w-72 flex-shrink-0 border-r border-gray-200 p-5 space-y-5">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Order Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select status</option>
                {Object.keys(STATUS_UPDATE_MSG).map((key) => (
                  <option key={key} value={key}>
                    {key.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-gray-400">
                {STATUS_UPDATE_MSG[selectedStatus]}
              </p>

              <button
                onClick={handleUpdateStatus}
                disabled={updating}
                className="mt-3 w-full bg-blue-500 text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-blue-600 disabled:opacity-40"
              >
                {updating ? "Updating..." : "Update"}
              </button>

              {updateError && (
                <p className="mt-2 text-xs text-red-600">{updateError}</p>
              )}
              {updateSuccess && (
                <p className="mt-2 text-xs text-emerald-600">{updateSuccess}</p>
              )}
            </div>

            {item?.status === "PROCESSING" && (
              <div className="pt-4 border-t border-gray-100">
                <button
                  onClick={handleSendPurchaseEmail}
                  disabled={sendingEmail}
                  className="w-full bg-gray-800 text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-gray-900 disabled:opacity-40"
                >
                  {sendingEmail ? "Sending..." : "Send Purchase Email"}
                </button>

                {emailError && (
                  <p className="mt-2 text-xs text-red-600">{emailError}</p>
                )}
                {emailSuccess && (
                  <p className="mt-2 text-xs text-emerald-600">
                    {emailSuccess}
                  </p>
                )}
              </div>
            )}

            <div className="pt-4 border-t border-gray-100">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Source Order ID
              </label>

              {sourceOrderId ? (
                <p className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {sourceOrderId}
                </p>
              ) : (
                <>
                  <input
                    type="text"
                    value={sourceOrderIdInput}
                    onChange={(e) => setSourceOrderIdInput(e.target.value)}
                    placeholder="Enter source order ID"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleAddSourceOrderId}
                    disabled={savingSourceOrderId || !sourceOrderIdInput.trim()}
                    className="mt-3 w-full bg-blue-500 text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-blue-600 disabled:opacity-40"
                  >
                    {savingSourceOrderId ? "Saving..." : "Save Source Order ID"}
                  </button>
                </>
              )}

              {sourceOrderIdError && (
                <p className="mt-2 text-xs text-red-600">
                  {sourceOrderIdError}
                </p>
              )}
            </div>

            <div className="pt-4 border-t border-gray-100">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Internal Tracking Number
              </label>

              {(() => {
                const existing = item?.shipment?.internalTrackings || [];
                const merged = [
                  ...existing,
                  ...addedTrackings.filter(
                    (t) => !existing.some((e) => e.id === t.id),
                  ),
                ];
                return merged.length > 0 ? (
                  <div className="mb-2 space-y-1">
                    {merged.map((t) => (
                      <p
                        key={t.id}
                        className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                      >
                        {t.trackingNo}
                      </p>
                    ))}
                  </div>
                ) : null;
              })()}

              <input
                type="text"
                value={trackingInput}
                onChange={(e) => setTrackingInput(e.target.value)}
                placeholder="Enter internal tracking number"
                disabled={!item?.shipment?.id}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
              <button
                onClick={handleAddTracking}
                disabled={
                  savingTracking || !trackingInput.trim() || !item?.shipment?.id
                }
                className="mt-3 w-full bg-blue-500 text-white rounded-md px-3 py-2 text-xs font-medium hover:bg-blue-600 disabled:opacity-40"
              >
                {savingTracking ? "Saving..." : "Add Tracking Number"}
              </button>

              {!item?.shipment?.id && (
                <p className="mt-1.5 text-xs text-gray-400">
                  No shipment linked to this item yet.
                </p>
              )}
              {trackingError && (
                <p className="mt-2 text-xs text-red-600">{trackingError}</p>
              )}
              {trackingSuccess && (
                <p className="mt-2 text-xs text-emerald-600">
                  {trackingSuccess}
                </p>
              )}
            </div>
          </div>

          {/* Right panel - reserved for future content (invoice edits, etc.) */}
          <div className="w-full sm:w-72 flex-shrink-0 border-r border-gray-200 p-5 space-y-5">
            Invoice <strong>#{item?.invoice?.invoiceNo}</strong>
            <div>
              {item?.invoice?.charges?.map((c, i) => (
                <Charge c={c} i={i} item={item} onUpdated={onUpdated} />
              ))}
            </div>
          </div>

          <div className="w-full sm:w-72 flex-shrink-0 border-r border-gray-200 p-5">
            Shipment <strong>#{item?.shipment?.shipmentNo}</strong>
            <div>
              Category: <strong>{item?.shipment?.category}</strong>
            </div>
            <div>
              Rate: <strong>{item?.shipment?.rate}/KG</strong>
            </div>
            <div>
              Total Weight:{" "}
              <strong>{item?.shipment?.totalWeight || 0} KG</strong>
            </div>
            <div>
              Total Price:{" "}
              <strong>{formatCurrency(item?.shipment?.totalPrice)}</strong>
            </div>
            <div className="mt-3 border-t border-gray-200 pt-3">
              <div className="flex flex-col gap-2">
                <label>Update Weight</label>
                <input
                  type={"text"}
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  disabled={weight === item?.shipment?.totalWeight}
                  className="border rounded bg-blue-500 text-white"
                  onClick={() => handleUpdateWeight()}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Charge = ({ c, i, item, onUpdated }) => {
  const [amount, setAmount] = useState(c.amount);
  const handleUpdate = async (charge) => {
    if (charge.type === "LDC_FEE") {
      const data = {
        amount: amount,
        chargeId: charge?.id,
        itemId: item?.id,
      };
      try {
        const res = await axiosInstance.post(
          `/admin/services/order/update-ldc`,
          data,
        );
        if (res.status === 200) {
          onUpdated?.();
        }
      } catch (error) {
        alert("Error!");
        console.log(error);
      }
    }
    return;
  };
  return (
    <div className="flex flex-col gap-2" id={i}>
      <label>{c.type}</label>
      <input
        id={c.type}
        type={"text"}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button
        disabled={amount === c.amount}
        className="border rounded bg-blue-500 text-white"
        onClick={() => handleUpdate(c)}
      >
        Update
      </button>
    </div>
  );
};

const formatCurrency = (value, currency = "BDT") =>
  `${currency} ${Number(value || 0).toLocaleString("en-BD", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const OrderItemRow = ({ item, onActionComplete }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // "approve" | "cancel" | null
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const handleConfirmAction = async () => {
    if (!confirmType) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await axiosInstance.get(
        `/admin/services/order/${confirmType}/${item.itemId}`,
      );
      setConfirmType(null);
      onActionComplete?.();
    } catch (err) {
      setActionError(
        err?.response?.data?.msg || `Failed to ${confirmType} order item.`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const paid = item.invoice?.transactions?.reduce(
    (sum, t) => sum + (t.status === "SUCCESS" ? Number(t.amount) : 0),
    0,
  );
  const due =
    Number(item.discountedTotal || 0) +
    Number(item.ldcFee || 0) +
    Number(item.shipment?.totalPrice || 0) -
    (paid || 0);

  function getAliCdnUrl(url) {
    const filename = url.split("/").pop().split("?")[0];
    return `https://img.alicdn.com/${filename}`;
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-start gap-4 p-2">
        {item.store === "A1688" ? (
          <img
            src={getAliCdnUrl(item.productImg)}
            alt={item.productTitle}
            className="w-20 h-20 rounded-md object-cover flex-shrink-0 bg-gray-100"
          />
        ) : (
          <img
            src={item.productImg}
            alt={item.productTitle}
            className="w-20 h-20 rounded-md object-cover flex-shrink-0 bg-gray-100"
          />
        )}

        <div className="grid grid-cols-2 gap-2 min-w-0">
          <div className="flex items-center gap-3 border-r border-gray-100 pr-2">
            <div className="min-w-0">
              <a
                href={item.productUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-1"
              >
                {item.productTitle}
              </a>
              <p className="mt-1 text-xs text-gray-500">
                Order <strong>#{item.order?.orderId}</strong> | Item{" "}
                <strong>#{item.itemId}</strong> | {item.store}
              </p>
              <p className="text-xs text-gray-500">
                Shipping: {item.shipment?.category || "N/A"} |{" "}
                {item.shipment?.shippingCompany?.name || "N/A"}
              </p>

              <div className="flex items-center gap-1 flex-wrap">
                <StatusBadge status={item.status} styles={STATUS_STYLES} />
                <StatusBadge
                  status={item.invoice?.status}
                  styles={INVOICE_STATUS_STYLES}
                />

                <div className="flex items-center gap-1 flex-wrap">
                  <p className="text-xs text-gray-600">
                    | {item.order?.user?.name || "N/A"} |
                  </p>

                  <p className="text-xs text-gray-600">
                    {item.order?.billingAddress?.phone || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pl-2">
            <div className="flex flex-col text-xs">
              <div className="flex items-center gap-1">
                <p className="text-gray-400">Qty: </p>
                <p className="font-medium text-gray-900">{item.totalQty}</p>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-gray-400">Item total:</p>
                <p className="font-medium text-gray-900">
                  {formatCurrency(item.discountedTotal, item.invoice?.currency)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-gray-400">Shipping total:</p>
                <p className="font-medium text-gray-900">
                  {formatCurrency(
                    item.shipment?.totalPrice || 0,
                    item.invoice?.currency,
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-gray-400">Due:</p>
                <p
                  className={`font-medium ${
                    due > 0 ? "text-red-600" : "text-emerald-600"
                  }`}
                >
                  {formatCurrency(due, item.invoice?.currency)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-gray-400">Placed: </p>
                <p className="font-medium text-gray-900">
                  {new Date(item.createdAt).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center gap-1 flex-shrink-0">
              {item?.status === "PENDING" &&
                item?.invoice?.status === "PAID" && (
                  <button
                    onClick={() => setConfirmType("approve")}
                    className="flex items-center bg-blue-500 text-white rounded-full px-2.5 py-0.5 text-xs font-medium hover:bg-blue-600"
                  >
                    Approve
                  </button>
                )}

              {item?.status === "PENDING" && (
                <button
                  onClick={() => setConfirmType("cancel")}
                  className="flex items-center bg-red-500 text-white rounded-full px-2.5 py-0.5 text-xs font-medium hover:bg-red-600"
                >
                  Cancel
                </button>
              )}

              <button
                onClick={() => setExpanded((v) => !v)}
                className="flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                View Details
              </button>

              {item?.status !== "CANCELLED" && (
                <button
                  onClick={() => setShowUpdateModal(true)}
                  className="flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-black hover:bg-gray-200"
                >
                  Manage Order
                </button>
              )}
              {item?.status === "AT_DESTINATION_WAREHOUSE" && (
                <button
                  onClick={() =>
                    window.open(
                      `https://core.alistore.com.bd/api/v1/admin/services/order/print-label?itemId=${item.itemId}`,
                      "_blank",
                    )
                  }
                  className="flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-black hover:bg-gray-200"
                >
                  Print Label
                </button>
              )}
            </div>
          </div>
          {/* <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            {expanded ? "Hide details" : "View details"}
          </button> */}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 space-y-3 text-xs">
          <div>
            <p className="font-semibold text-gray-700 mb-1">Variations</p>
            {item.variations?.map((v) => (
              <div
                key={v.id}
                className="flex items-center gap-2 text-gray-600 py-0.5"
              >
                {item.store === "A1688" ? (
                  <img
                    src={getAliCdnUrl(v.img)}
                    alt=""
                    className="w-8 h-8 rounded object-cover"
                  />
                ) : (
                  <img
                    src={v.img}
                    alt=""
                    className="w-8 h-8 rounded object-cover"
                  />
                )}
                <span>
                  {v.properties?.map((p) => `${p.name}: ${p.value}`).join(", ")}{" "}
                  | Qty {v.quantity} |{" "}
                  {formatCurrency(v.totalPrice, item.invoice?.currency)}
                </span>
              </div>
            ))}
          </div>

          {item.shipment && (
            <div>
              <p className="font-semibold text-gray-700 mb-1">Shipment</p>
              <p className="text-gray-600">
                {item.shipment.category} via{" "}
                {item.shipment.shippingCompany?.name} |{" "}
                {formatCurrency(item.shipment.rate, item.invoice?.currency)}
              </p>
            </div>
          )}

          {item.invoice?.charges?.length > 0 && (
            <div>
              <p className="font-semibold text-gray-700 mb-1">Charges</p>
              {item.invoice.charges.map((c) => (
                <div key={c.id} className="flex justify-between text-gray-600">
                  <span>{c.property}</span>
                  <span>{formatCurrency(c.amount, c.currency)}</span>
                </div>
              ))}
            </div>
          )}

          {item.orderItemTimeLines?.length > 0 && (
            <div>
              <p className="font-semibold text-gray-700 mb-1">Timeline</p>
              {item.orderItemTimeLines.map((t) => (
                <div key={t.id} className="text-gray-600">
                  <span className="font-medium">{t.statusValue}</span> - {t.msg}{" "}
                  ({formatDate(t.createdAt)})
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {actionError && (
        <div className="border-t border-red-100 bg-red-50 text-red-700 text-xs px-4 py-2">
          {actionError}
        </div>
      )}

      <ConfirmModal
        open={!!confirmType}
        title={
          confirmType === "approve"
            ? "Approve order item?"
            : "Cancel order item?"
        }
        message={
          confirmType === "approve"
            ? "This will mark the order item as approved and move it into processing."
            : "This will cancel the order item. This action cannot be undone."
        }
        confirmColor={
          confirmType === "approve"
            ? "bg-blue-500 hover:bg-blue-600"
            : "bg-red-500 hover:bg-red-600"
        }
        loading={actionLoading}
        onConfirm={handleConfirmAction}
        onClose={() => !actionLoading && setConfirmType(null)}
      />

      <OrderUpdateModal
        open={showUpdateModal}
        item={item}
        onClose={() => setShowUpdateModal(false)}
        onUpdated={onActionComplete}
      />
    </div>
  );
};

export const Shipment = () => {
  const [orderItems, setOrderItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalItems: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // debounced value actually sent to the API
  const [statusFilter, setStatusFilter] = useState("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("");

  // debounce the search box so we're not firing a request on every keystroke
  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const fetchOrders = useCallback(
    async (page) => {
      setLoading(true);
      setError(null);
      try {
        const params = { page };
        if (search) params.search = search; // matches itemId or orderId
        if (statusFilter) params.status = statusFilter;
        if (invoiceStatusFilter) params.invoiceStatus = invoiceStatusFilter;

        const res = await axiosInstance.get(
          `/admin/services/shipment/get-all-items`,
          { params },
        );
        setOrderItems(res.data.orderItems || []);
        setPagination(res.data.pagination);
      } catch (err) {
        setError(err?.response?.data?.msg || "Failed to load order items.");
      } finally {
        setLoading(false);
      }
    },
    [search, statusFilter, invoiceStatusFilter],
  );

  useEffect(() => {
    fetchOrders(1); // filters/search changed -> fetchOrders identity changes -> back to page 1
  }, [fetchOrders]);

  const goToPage = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    fetchOrders(page);
  };

  return (
    <div className="mx-auto sm:px-4 lg:px-0">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">
          Orders ({pagination.totalItems})
        </h1>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by Item ID or Order ID"
            className="w-full sm:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_STYLES).map((status) => (
              <option key={status} value={status}>
                {status.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <select
            value={invoiceStatusFilter}
            onChange={(e) => setInvoiceStatusFilter(e.target.value)}
            className="w-full sm:w-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All invoice statuses</option>
            {Object.keys(INVOICE_STATUS_STYLES).map((status) => (
              <option key={status} value={status}>
                {status.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-24 bg-gray-100 rounded-lg animate-pulse"
            />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      {!loading && !error && orderItems.length === 0 && (
        <div className="text-center py-16 text-gray-500 text-sm">
          No order items found.
        </div>
      )}

      {!loading && !error && orderItems.length > 0 && (
        <div className="space-y-3">
          {orderItems.map((item) => (
            <OrderItemRow
              key={item.id}
              item={item}
              onActionComplete={() => fetchOrders(pagination.page)}
            />
          ))}
        </div>
      )}

      {!loading && !error && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => goToPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => goToPage(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
